import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Trophy, Target, Zap, Flame, Activity,
  ChevronDown, Crown, Medal, Award,
} from "lucide-react";
import axios from "axios";
import StratagemPicker from "@/components/leaderboard/StratagemPicker";
import { PodiumMeta, formatSortValue, sortLabel, getStratagemLabel } from "@/components/leaderboard/LeaderboardHelpers";
import { play as sfx, playHover } from "@/utils/sounds";
import {
  fadeIn, fadeInUp, transition, transitionDelayed, transitionMedium,
  transitionPodium, transitionTable, podiumEntrance, tableRowEntrance,
} from "@/utils/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SortIcon = ({ sortId }) => {
  if (sortId === "accuracy") return <Target className="h-3 w-3" />;
  if (sortId === "speed") return <Zap className="h-3 w-3" />;
  if (sortId === "streak") return <Flame className="h-3 w-3" />;
  if (sortId === "volume") return <Activity className="h-3 w-3" />;
  if (sortId === "spm") return <Zap className="h-3 w-3" />;
  if (sortId === "completed") return <Trophy className="h-3 w-3" />;
  return null;
};

const RankIcon = ({ rank }) => {
  if (rank === 0) return <Crown className="h-5 w-5 text-[#FFE800]" />;
  if (rank === 1) return <Medal className="h-5 w-5 text-gray-300" />;
  if (rank === 2) return <Award className="h-5 w-5 text-amber-700" />;
  return null;
};

const SORT_IDS = ["accuracy", "speed", "streak", "volume"];
const SORT_LABELS = { accuracy: "Accuracy", speed: "Fastest Time", streak: "Longest Streak", volume: "Most Drills" };
const RF_SORT_IDS = ["spm", "completed", "streak", "accuracy"];
const RF_SORT_LABELS = { spm: "Stratagems/Min", completed: "Most Completed", streak: "Longest Streak", accuracy: "Best Accuracy" };
const PODIUM_ORDER = [1, 0, 2];

const Leaderboard = () => {
  const [view, setView] = useState("overall");
  const [sort, setSort] = useState("accuracy");
  const [category, setCategory] = useState("");
  const [stratagemId, setStratagemId] = useState("");
  const [stratagemSearch, setStratagemSearch] = useState("");

  const [data, setData] = useState(null);
  const [available, setAvailable] = useState({ stratagems: [], categories: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/leaderboard/stratagems`)
      .then(res => setAvailable(res.data))
      .catch(() => {});
  }, []);

  const fetchLeaderboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ view, sort });
      if (view === "stratagem" && stratagemId) params.set("stratagem_id", stratagemId);
      if (view === "overall" && category) params.set("category", category);
      // rapid_fire uses view=rapid_fire in the API
      const res = await axios.get(`${API}/leaderboard?${params}`);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [view, sort, stratagemId, category]);

  useEffect(() => { fetchLeaderboard(); }, [fetchLeaderboard]);

  useEffect(() => {
    if (view === "stratagem" && !stratagemId && available.stratagems.length > 0) {
      setStratagemId(available.stratagems[0].id);
    }
  }, [view, stratagemId, available.stratagems]);

  const entries = data?.entries || [];
  const top3 = entries.slice(0, 3);
  const rest = entries.slice(3);

  const filteredStratagems = available.stratagems.filter(s =>
    s.name.toLowerCase().includes(stratagemSearch.toLowerCase()) ||
    s.category.toLowerCase().includes(stratagemSearch.toLowerCase())
  );

  const activeStratagemName = view === "stratagem" && data?.stratagem
    ? data.stratagem.name
    : null;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10 md:px-12" data-testid="leaderboard-page">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={transitionMedium}
        className="mb-8"
      >
        <p className="text-xs uppercase tracking-[0.4em] text-gray-500" data-testid="leaderboard-kicker">
          Super Earth Command
        </p>
        <h1
          className="mt-2 font-headings text-5xl md:text-6xl uppercase tracking-[0.2em] text-[#FFE800] glow-yellow"
          data-testid="leaderboard-title"
        >
          Leaderboard
        </h1>
        {activeStratagemName && (
          <p className="mt-1 font-data text-lg text-gray-400" data-testid="leaderboard-stratagem-label">
            {data.stratagem.category} &mdash; {activeStratagemName}
          </p>
        )}
      </motion.div>

      {/* Filter Panel */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={transitionDelayed}
        className="hud-card p-5 mb-8 relative z-30"
        data-testid="leaderboard-filters"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* View Toggle */}
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-gray-500 mb-2 font-headings">View</label>
            <div className="flex gap-2" data-testid="filter-view">
              <button
                onClick={() => { sfx("tab"); setView("overall"); setSort("accuracy"); setStratagemId(""); }}
                onMouseEnter={playHover}
                className={`flex-1 border px-3 py-2 text-xs font-headings uppercase tracking-widest transition-colors duration-150 ${
                  view === "overall"
                    ? "border-[#FFE800] text-[#FFE800] bg-[#FFE800]/5"
                    : "border-white/10 text-gray-400 hover:text-[#FFE800] hover:border-[#FFE800]/40"
                }`}
                data-testid="filter-view-overall"
              >
                Overall
              </button>
              <button
                onClick={() => { sfx("tab"); setView("stratagem"); setSort("accuracy"); }}
                onMouseEnter={playHover}
                className={`flex-1 border px-3 py-2 text-xs font-headings uppercase tracking-widest transition-colors duration-150 ${
                  view === "stratagem"
                    ? "border-[#FFE800] text-[#FFE800] bg-[#FFE800]/5"
                    : "border-white/10 text-gray-400 hover:text-[#FFE800] hover:border-[#FFE800]/40"
                }`}
                data-testid="filter-view-stratagem"
              >
                Per-Stratagem
              </button>
              <button
                onClick={() => { sfx("tab"); setView("rapid_fire"); setSort("spm"); setStratagemId(""); }}
                onMouseEnter={playHover}
                className={`flex-1 border px-3 py-2 text-xs font-headings uppercase tracking-widest transition-colors duration-150 ${
                  view === "rapid_fire"
                    ? "border-[#FFE800] text-[#FFE800] bg-[#FFE800]/5"
                    : "border-white/10 text-gray-400 hover:text-[#FFE800] hover:border-[#FFE800]/40"
                }`}
                data-testid="filter-view-rapid-fire"
              >
                Rapid Fire
              </button>
            </div>
          </div>

          {/* Category / Stratagem filter (hidden for rapid fire) */}
          {view !== "rapid_fire" && (
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-gray-500 mb-2 font-headings">
              {view === "stratagem" ? "Stratagem" : "Category"}
            </label>
            {view === "overall" ? (
              <div className="relative">
                <select
                  value={category}
                  onChange={e => { sfx("click"); setCategory(e.target.value); }}
                  className="w-full appearance-none bg-black/60 border border-white/10 px-3 py-2 text-sm font-data text-white focus:border-[#FFE800]/60 focus:outline-none"
                  data-testid="filter-category"
                >
                  <option value="">All Categories</option>
                  {available.categories.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-2.5 h-4 w-4 text-gray-500 pointer-events-none" />
              </div>
            ) : (
              <StratagemPicker
                stratagems={filteredStratagems}
                value={stratagemId}
                search={stratagemSearch}
                onSearch={setStratagemSearch}
                onChange={setStratagemId}
              />
            )}
          </div>
          )}

          {/* Sort By */}
          <div className={view === "rapid_fire" ? "sm:col-span-2 lg:col-span-3" : "sm:col-span-2 lg:col-span-2"}>
            <label className="block text-xs uppercase tracking-[0.2em] text-gray-500 mb-2 font-headings">Sort By</label>
            <div className="flex flex-wrap gap-2" data-testid="filter-sort">
              {(view === "rapid_fire" ? RF_SORT_IDS : SORT_IDS).map(sid => (
                <button
                  key={sid}
                  onClick={() => { sfx("tab"); setSort(sid); }}
                  onMouseEnter={playHover}
                  className={`flex items-center gap-1.5 border px-3 py-2 text-xs font-headings uppercase tracking-widest transition-colors duration-150 ${
                    sort === sid
                      ? "border-[#FFE800] text-[#FFE800] bg-[#FFE800]/5"
                      : "border-white/10 text-gray-400 hover:text-[#FFE800] hover:border-[#FFE800]/40"
                  }`}
                  data-testid={`filter-sort-${sid}`}
                >
                  <SortIcon sortId={sid} /> {(view === "rapid_fire" ? RF_SORT_LABELS : SORT_LABELS)[sid]}
                </button>
              ))}
            </div>
          </div>
        </div>
        {sort === "accuracy" && (
          <p className="mt-3 text-xs font-data text-gray-500" data-testid="filter-threshold-note">
            Minimum {view === "stratagem" ? "5" : "10"} attempts required to qualify for accuracy rankings.
          </p>
        )}
      </motion.div>

      {/* Loading / Empty */}
      {loading ? (
        <div className="text-center py-20" data-testid="leaderboard-loading">
          <div className="inline-block h-8 w-8 border-2 border-[#FFE800] border-t-transparent rounded-full animate-spin" />
          <p className="mt-4 text-sm text-gray-500 font-data">LOADING COMBAT RECORDS...</p>
        </div>
      ) : entries.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="hud-card clip-corners p-12 text-center"
          data-testid="leaderboard-empty"
        >
          <Trophy className="h-12 w-12 text-gray-600 mx-auto mb-4" />
          <p className="font-headings text-2xl uppercase tracking-widest text-gray-500">No Rankings Yet</p>
          <p className="mt-2 text-sm text-gray-600">
            {view === "stratagem" ? "No one has drilled this stratagem yet. Be the first!" : "Complete some drills to claim your spot."}
          </p>
        </motion.div>
      ) : (
        <>
          {/* Top 3 Podium */}
          {top3.length > 0 && (
            <motion.div
              {...fadeIn}
              transition={transitionPodium}
              className="mb-8"
              data-testid="leaderboard-podium"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                {PODIUM_ORDER.map((podiumIdx, layoutIdx) => {
                  const entry = top3[podiumIdx];
                  if (!entry) return <div key={layoutIdx} />;
                  const isFirst = podiumIdx === 0;

                  return (
                    <motion.div
                      key={entry.callsign}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={podiumEntrance(layoutIdx)}
                      className={`hud-card clip-corners ${isFirst ? "p-8" : "p-6"} ${
                        isFirst
                          ? "border-[#FFE800]/50 bg-[#FFE800]/[0.03]"
                          : "border-white/10"
                      }`}
                      data-testid={`leaderboard-podium-${podiumIdx}`}
                    >
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <span className={`font-data text-xs uppercase tracking-widest ${isFirst ? "text-[#FFE800]" : "text-gray-500"}`}>
                            Rank #{entry.rank}
                          </span>
                          <RankIcon rank={podiumIdx} />
                        </div>
                        <h3
                          className={`font-headings text-2xl md:text-3xl uppercase tracking-widest ${isFirst ? "text-[#FFE800] glow-yellow" : "text-white"}`}
                          data-testid={`podium-callsign-${podiumIdx}`}
                        >
                          {entry.callsign}
                        </h3>
                      </div>
                      <div className="mt-4 space-y-2">
                        <div className="flex justify-between items-baseline">
                          <span className="text-xs text-gray-500 uppercase tracking-widest font-headings">{sortLabel(sort)}</span>
                          <span className={`font-data text-xl ${isFirst ? "text-[#FFE800]" : "text-white"}`} data-testid={`podium-value-${podiumIdx}`}>
                            {formatSortValue(entry, sort)}
                          </span>
                        </div>
                        <PodiumMeta entry={entry} sort={sort} view={view} />
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Full Rankings Table */}
          {rest.length > 0 && (
            <motion.div
              {...fadeInUp}
              transition={transitionTable}
              className="hud-card clip-corners overflow-hidden"
              data-testid="leaderboard-table"
            >
              <div className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-white/10 text-xs font-headings uppercase tracking-widest text-gray-500">
                <div className="col-span-1">Rank</div>
                <div className="col-span-3">Callsign</div>
                <div className="col-span-2 text-right">{sortLabel(sort)}</div>
                <div className="col-span-2 text-right hidden md:block">{view === "rapid_fire" ? "Best Streak" : "Accuracy"}</div>
                <div className="col-span-2 text-right hidden md:block">{view === "rapid_fire" ? "Sessions" : "IPM"}</div>
                <div className="col-span-2 text-right hidden md:block">{view === "rapid_fire" ? "Total" : view === "overall" ? "Stratagem" : "Drills"}</div>
              </div>
              {rest.map((entry, i) => (
                <motion.div
                  key={`${entry.callsign}-${entry.rank}`}
                  {...fadeIn}
                  transition={tableRowEntrance(i)}
                  className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-white/5 hover:bg-[#FFE800]/[0.03] hover:border-[#FFE800]/20 transition-colors duration-150 group"
                  data-testid={`leaderboard-row-${entry.rank}`}
                >
                  <div className="col-span-1 font-data text-sm text-gray-500 group-hover:text-[#FFE800] transition-colors duration-150">
                    {entry.rank}
                  </div>
                  <div className="col-span-3 font-data text-sm text-white group-hover:text-[#FFE800] transition-colors duration-150 truncate" data-testid={`row-callsign-${entry.rank}`}>
                    {entry.callsign}
                  </div>
                  <div className="col-span-2 font-data text-sm text-[#FFE800] text-right" data-testid={`row-value-${entry.rank}`}>
                    {formatSortValue(entry, sort)}
                  </div>
                  <div className="col-span-2 font-data text-sm text-gray-400 text-right hidden md:block">
                    {view === "rapid_fire" ? (entry.best_streak ?? 0) : `${entry.accuracy}%`}
                  </div>
                  <div className="col-span-2 font-data text-sm text-gray-400 text-right hidden md:block">
                    {view === "rapid_fire" ? (entry.sessions ?? 0) : entry.ipm}
                  </div>
                  <div className="col-span-2 font-data text-xs text-gray-500 text-right hidden md:block truncate">
                    {view === "rapid_fire" ? (entry.total_completed ?? 0) : view === "overall" ? getStratagemLabel(entry, sort) : entry.successes}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {data?.total > 0 && (
            <p className="mt-4 text-xs text-gray-600 font-data text-right" data-testid="leaderboard-total">
              {data.total} ranked helldiver{data.total !== 1 ? "s" : ""}
            </p>
          )}
          <p className="mt-2 text-xs text-gray-500 font-data text-right" data-testid="leaderboard-requirements">
            Requires: signed in, callsign set, {sort === "accuracy" ? (view === "stratagem" ? "5+" : "10+") + " attempts" : "1+ success"}
          </p>
        </>
      )}
    </div>
  );
};

export default Leaderboard;
